/**
 * Split loads: one truck run delivering to several places, dispatched as one
 * PDF per drop. Each PDF repeats the whole run in its comments, e.g.
 *   "SPLIT 2,300 LA VERNE 3,300 CAMARILLO 2,200 VAN NUYS"
 * which is how the drops are tied back together.
 */

export interface SplitStop {
  gallons: number;
  place: string;
}

export interface SplitInfo {
  /** Same for every drop of the run (broker + date + the run itself) */
  groupId: string;
  /** Number of drops (2 = 2-way split) */
  ways: number;
  /** This drop's place in the run, 1-based; null when it can't be told */
  position: number | null;
  stops: SplitStop[];
  totalGallons: number;
}

const NUMBER = /^\d{1,3}(?:,\d{3})*(?:\.\d+)?$|^\d+(?:\.\d+)?$/;

/**
 * The drops listed after "SPLIT" (up to the next "//"), or [] when the text
 * doesn't describe a split.
 */
export const parseSplitStops = (text: string): SplitStop[] => {
  const match = (text || '').match(/\bSPLIT\b([^/]*)/i);
  if (!match) return [];
  const words = match[1].trim().split(/\s+/).filter(Boolean);

  const stops: SplitStop[] = [];
  let current: SplitStop | null = null;
  for (const word of words) {
    if (NUMBER.test(word)) {
      if (current && current.place) stops.push(current);
      current = { gallons: parseFloat(word.replace(/,/g, '')), place: '' };
    } else if (current) {
      current.place = current.place ? `${current.place} ${word}` : word;
    }
  }
  if (current && current.place) stops.push(current);
  return stops.length >= 2 ? stops : [];
};

const simplify = (value: string) => value.toUpperCase().replace(/[^A-Z0-9]+/g, ' ').trim();

/**
 * Split details for one drop, or null when the dispatch isn't a split.
 * The drop is found by place name in the destination, then by gallons.
 */
export const readSplit = (args: {
  brokerKey: string;
  deliveryDate: string;
  destination: string;
  gallons: number;
  text: string;
}): SplitInfo | null => {
  const stops = parseSplitStops(args.text);
  if (!stops.length) return null;

  const destination = ` ${simplify(args.destination)} `;
  let index = stops.findIndex(stop => destination.includes(` ${simplify(stop.place)} `));
  if (index < 0) {
    const byGallons = stops.filter(stop => stop.gallons === args.gallons);
    index = byGallons.length === 1 ? stops.indexOf(byGallons[0]) : -1;
  }

  const run = stops.map(stop => `${stop.gallons}${simplify(stop.place).replace(/ /g, '')}`).join('-');
  return {
    groupId: `split-${args.brokerKey}-${args.deliveryDate || 'nodate'}-${run}`.toLowerCase(),
    ways: stops.length,
    position: index >= 0 ? index + 1 : null,
    stops,
    totalGallons: stops.reduce((sum, stop) => sum + stop.gallons, 0)
  };
};

/** "Split 1/3" style label. */
export const splitLabel = (split: Pick<SplitInfo, 'ways' | 'position'>): string =>
  split.position ? `Split ${split.position}/${split.ways}` : `${split.ways}-way split`;

/** "2,300 La Verne · 3,300 Camarillo · 2,200 Van Nuys" */
export const describeStops = (stops: SplitStop[]): string =>
  stops
    .map(
      stop =>
        `${stop.gallons.toLocaleString('en-US')} ${stop.place
          .toLowerCase()
          .replace(/\b\w/g, letter => letter.toUpperCase())}`
    )
    .join(' · ');
